import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroImages } from '../mock';
import TrustBadge from './TrustBadge';
import api, { mediaUrl } from '../lib/api';

const THEMES = {
  floral: { section: `linear-gradient(rgba(0,0,0,0.22),rgba(0,0,0,0.3)), url(${heroImages.hero}) center/cover`,
    card: 'radial-gradient(circle at 50% 40%, rgba(255,240,232,0.92) 0%, rgba(245,199,205,0.72) 60%, rgba(245,199,205,0.4) 100%)', dark: true },
  rose: { section: 'linear-gradient(135deg,#F7D5DA 0%,#E79AA6 100%)',
    card: 'radial-gradient(circle at 50% 40%, rgba(255,255,255,0.9) 0%, rgba(255,240,232,0.7) 70%)', dark: true },
  burgundy: { section: 'linear-gradient(135deg,#3a121b 0%,#7A2233 100%)',
    card: 'radial-gradient(circle at 50% 40%, rgba(122,34,51,0.66) 0%, rgba(58,18,27,0.72) 80%)', dark: false },
  cream: { section: 'linear-gradient(135deg,#FBF3EC 0%,#F3E2D6 100%)',
    card: 'radial-gradient(circle at 50% 40%, rgba(255,255,255,0.9) 0%, rgba(247,213,218,0.55) 80%)', dark: true },
  dusk: { section: 'linear-gradient(135deg,#5A1826 0%,#C97C86 100%)',
    card: 'radial-gradient(circle at 50% 40%, rgba(74,23,33,0.55) 0%, rgba(58,18,27,0.68) 85%)', dark: false },
};

const DEFAULT_SLIDE = {
  id: 'default', title: 'Awaken In Love And', highlight: 'Awareness',
  subtitle: "Join us for a transformative journey of a lifetime through self-knowledge, awareness, meditation, and devotion — in nature's embrace.",
  bg_theme: 'floral',
  primary_label: 'Explore Retreats', primary_link: '/events',
  secondary_label: 'About Ashram', secondary_link: '#about',
};

const isInternal = (l) => l && l.startsWith('/');

const Cta = ({ label, link, outline, dark }) => {
  if (!label || !link) return null;
  const cls = outline ? (dark ? 'pill-btn-outline' : 'pill-btn-outline !border-cream/70 !text-cream') : 'pill-btn';
  const content = <>{label}{!outline && <ArrowUpRight className="w-4 h-4" />}</>;
  if (isInternal(link)) return <Link to={link} className={cls}>{content}</Link>;
  return <a href={link} className={cls} {...(link.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>{content}</a>;
};

const isMobile = () => { try { return window.matchMedia('(max-width: 767px), (hover: none) and (pointer: coarse)').matches; } catch { return false; } };

const Slide = ({ s }) => {
  const theme = THEMES[s.bg_theme] || THEMES.floral;
  // Explicit background type; fall back to inferring from fields for older slides.
  const bgType = s.bg_type || (s.video_url ? 'video' : (s.image ? 'photo' : 'theme'));
  const hasVideo = bgType === 'video' && !!s.video_url;
  const hasPhoto = (bgType === 'photo' && !!s.image) || (bgType === 'video' && !!s.image);
  const showVideo = hasVideo && !isMobile();
  const hasMedia = hasVideo || hasPhoto;
  const useDarkText = hasMedia ? false : theme.dark;
  const titleColor = useDarkText ? 'text-burgundy' : 'text-cream';
  const subColor = useDarkText ? 'text-burgundy/85' : 'text-cream/90';
  const cardBg = hasMedia
    ? 'radial-gradient(circle at 50% 40%, rgba(74,23,33,0.30) 0%, rgba(74,23,33,0.48) 85%)'
    : theme.card;
  const posterBg = hasPhoto
    ? `linear-gradient(rgba(0,0,0,0.30),rgba(0,0,0,0.44)), url(${mediaUrl(s.image)}) center/cover`
    : theme.section;
  return (
    <div className="relative w-full h-full">
      {showVideo ? (
        <>
          <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline
            poster={hasPhoto ? mediaUrl(s.image) : undefined} data-testid="hero-video">
            <source src={mediaUrl(s.video_url)} type="video/mp4" />
          </video>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0.30),rgba(0,0,0,0.44))' }} />
        </>
      ) : (
        <div className="absolute inset-0 bg-center bg-cover" style={{ background: posterBg }} />
      )}
      <div className="absolute inset-x-0 top-0 bottom-36 md:bottom-28 flex items-center justify-center px-6 overflow-hidden">
        <div className="relative w-full max-w-[640px] max-h-full py-12 md:py-14 px-14 sm:px-20 md:px-24 text-center petal-blob overflow-y-auto"
          style={{ background: cardBg, backdropFilter: 'blur(6px)', boxShadow: '0 30px 80px -30px rgba(74,23,33,0.35)' }}>
          <h1 className={`font-serif-display ${titleColor} text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight break-words hyphens-auto`} style={!useDarkText ? { textShadow: '0 2px 16px rgba(0,0,0,0.55)' } : undefined}>{s.title}</h1>
          {s.highlight && <div className="font-italic-display text-rose-italic text-4xl sm:text-5xl md:text-6xl mt-2 break-words leading-[1.05]">{s.highlight}</div>}
          {s.subtitle && <p className={`${subColor} text-sm md:text-[17px] leading-relaxed max-w-md mx-auto mt-5 break-words`} style={!useDarkText ? { textShadow: '0 1px 10px rgba(0,0,0,0.55)' } : undefined}>{s.subtitle}</p>}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Cta label={s.primary_label} link={s.primary_link} dark={useDarkText} />
            <Cta label={s.secondary_label} link={s.secondary_link} outline dark={useDarkText} />
          </div>
          <div className="mt-6 flex justify-center">
            <TrustBadge variant="light" to="#testimonials" testid="hero-trust-badge" />
          </div>
        </div>
      </div>
    </div>
  );
};

const VisionLine = () => (
  <div className="absolute inset-x-0 bottom-12 md:bottom-14 z-10 px-6 flex justify-center pointer-events-none" data-testid="hero-vision">
    <div className="max-w-3xl text-center">
      <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-cream/80">Our Vision</div>
      <p className="mt-1.5 font-italic-display text-cream text-base md:text-2xl leading-snug" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.6)' }} data-testid="hero-vision-text">
        To bring love (Prem) and awareness (Dhyan) into every home and every heart, inspiring the awakening of every being on Earth.
      </p>
    </div>
  </div>
);

const Hero = () => {
  const [slides, setSlides] = useState([DEFAULT_SLIDE]);
  const [autoplay, setAutoplay] = useState(6000);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    api.get('/home/hero').then((r) => {
      const s = (r.data?.slides || []);
      if (s.length) setSlides(s);
      if (r.data?.autoplay_ms) setAutoplay(r.data.autoplay_ms);
    }).catch(() => {});
  }, []);

  const go = useCallback((n) => setIdx((p) => (n + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => { if (!paused.current) setIdx((p) => (p + 1) % slides.length); }, autoplay);
    return () => clearInterval(t);
  }, [slides.length, autoplay]);

  const cur = slides[Math.min(idx, slides.length - 1)];

  return (
    <section id="home" className="relative w-full" data-testid="home-hero"
      onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}>
      <div className="relative w-full h-[860px] md:h-[780px] overflow-hidden">
        <Slide s={cur} key={cur.id || idx} />
        <VisionLine />

        {slides.length > 1 && (
          <>
            <button onClick={() => go(idx - 1)} aria-label="Previous slide" data-testid="hero-prev"
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-cream/80 hover:bg-cream text-mahogany flex items-center justify-center shadow-lg transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => go(idx + 1)} aria-label="Next slide" data-testid="hero-next"
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-cream/80 hover:bg-cream text-mahogany flex items-center justify-center shadow-lg transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-testid="hero-dots">
              {slides.map((s, i) => (
                <button key={s.id || i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`hero-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === idx ? 'w-7 bg-cream' : 'w-2.5 bg-cream/50 hover:bg-cream/80'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Hero;
